import { useId, useLayoutEffect } from "react"
import type { ReactNode } from "react"
import { Select, TextField } from "@smooth/design-system-components"
import {
  DETAIL_SPLIT_QUERY,
  DetailFrame,
  DetailSection,
  SectionJumpBar,
  useDetailSections,
} from "../../components/detailLayout"
import type { EvaluationCase, Scenario } from "../types"
import type { ValidatedField } from "../validation"
import { ROUTING_MODE_LABELS, asOfLabel } from "./CaseDetailParts"
import { fromLocalInput, toLocalInput } from "./ScenarioRecordsForm"

// 検証ケースの外枠。督促ケース詳細（/admin/dunning-cases/[id]）と同じヘッダー・左右が別々にスクロールする2列・
// 右のセクションジャンプで出す（部品は detailLayout を督促ケース詳細と共有する）。
// 左は督促チャットだけ（ペインごと1つのスクロール）、右は期待（期待・生成・結果）→ 支払約束 → ユーザー・遅延状況・契約・メモ・交渉記録。
// 枠の上にページの見出し・タブがあるので、枠は画面の残りの高さに収める（ページ自体はスクロールさせない）

/** 右のペインのセクション。督促ケース詳細の並びのうち、検証ケースが持つものだけ */
export const CASE_SECTIONS = [
  { value: "user", label: "ユーザー" },
  { value: "delinquency", label: "遅延状況" },
  { value: "contracts", label: "契約" },
  { value: "memos", label: "メモ" },
  { value: "contact_logs", label: "交渉記録" },
] as const

export type CaseSection = (typeof CASE_SECTIONS)[number]["value"]

/** 右のセクションの id（督促ケース詳細の dunning-detail-* とは別にする） */
export const caseSectionId = (value: CaseSection) => `evaluation-detail-${value}`

/** 外枠に置くもののうち、ケースの画面（CaseWorkspace）が決めるもの */
export type CaseFrameSlots = {
  /** ヘッダー左端の戻る操作（「← 一覧」など）。無ければ出さない */
  onBack?: () => void
  backLabel?: string
  /** ヘッダー右端の操作（編集・保存・「…」メニュー） */
  actions?: ReactNode
  /** ヘッダーの下の段（保存・生成の結果の知らせ） */
  headerExtra?: ReactNode
  /** 右のペインの先頭（お客様の流れのタイムライン、読み込み・続きの下書きの注意、しまったケースの知らせ） */
  rightHead?: ReactNode
  /** 期待の下に置くもの（プロンプトの比較の結果・生成・生成結果・生成履歴） */
  review?: ReactNode
}

type HeaderProps = CaseFrameSlots & {
  record: EvaluationCase
  /** 編集中は、ケース名・時点・送るきっかけをヘッダーの中で直せる */
  editing?: {
    errors: Partial<Record<ValidatedField, string>>
    onChange: (patch: Partial<EvaluationCase>) => void
  }
}

/** ヘッダーのボタンの大きさ。中身（左右のペイン）の高さを取るため、ケース名の1行に収まる一回り小さいボタンにする */
export const HEADER_BUTTON_CLASS = "!h-9 !px-4"

/** 編集中のヘッダーの入力欄1組。見出しを入力欄の左に置き、エラーは入力欄の下に出す */
function InlineField({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: (field: { id: string; describedBy?: string }) => ReactNode
}) {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <div className="flex min-w-0 items-start gap-3">
      <label
        htmlFor={id}
        className="flex h-control-height shrink-0 items-center whitespace-nowrap text-caption font-bold text-text-default"
      >
        {label}
      </label>
      <div className="flex min-w-0 flex-col gap-1">
        {children({ id, describedBy: error ? errorId : undefined })}
        {error && (
          <p
            id={errorId}
            className="rounded-md bg-bg-error-soft px-2 py-1 text-caption text-text-error"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * 検証ケースのヘッダー。1行に「← 一覧」・ケース名・右端の操作（保存・「…」）を出し、
 * 表示中はケース名の下に時点と送るきっかけを小さく1行で、編集中はその下に時点と送るきっかけの入力欄を1行で出す
 */
export function CaseHeader({
  record,
  editing,
  onBack,
  backLabel = "← 一覧",
  actions,
  headerExtra,
}: HeaderProps) {
  const { scenario } = record
  const titleErrorId = useId()
  const patchScenario = (patch: Partial<Scenario>) =>
    editing?.onChange({ scenario: { ...scenario, ...patch } })
  return (
    <div className="px-4 py-2">
      <div className="flex items-center gap-x-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="shrink-0 whitespace-nowrap text-body font-bold text-text-accent hover:opacity-70"
          >
            {backLabel}
          </button>
        )}
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex max-w-[48rem] flex-col gap-1">
              <TextField
                aria-label="ケース名"
                maxLength={150}
                value={record.title}
                error={!!editing.errors.title}
                aria-invalid={editing.errors.title ? true : undefined}
                aria-describedby={editing.errors.title ? titleErrorId : undefined}
                onChange={(e) => editing.onChange({ title: e.target.value })}
              />
              {editing.errors.title && (
                <p id={titleErrorId} className="text-caption text-text-error">
                  {editing.errors.title}
                </p>
              )}
            </div>
          ) : (
            <>
              <h1 className="truncate text-heading-md font-bold text-text-default">
                {record.title}
              </h1>
              <p className="truncate text-caption text-text-muted">
                {asOfLabel(scenario.asOf)} · {ROUTING_MODE_LABELS[scenario.routingMode]}
              </p>
            </>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {editing && (
        <div
          role="group"
          aria-label="時点と送るきっかけ"
          className="mt-2 flex flex-wrap items-start gap-x-8 gap-y-2"
        >
          <InlineField label="時点" error={editing.errors.asOf}>
            {({ id, describedBy }) => (
              <TextField
                id={id}
                type="datetime-local"
                error={!!editing.errors.asOf}
                aria-invalid={editing.errors.asOf ? true : undefined}
                aria-describedby={describedBy}
                value={toLocalInput(scenario.asOf)}
                onChange={(e) => patchScenario({ asOf: fromLocalInput(e.target.value) })}
              />
            )}
          </InlineField>
          <InlineField label="送るきっかけ">
            {({ id }) => (
              <Select
                id={id}
                value={scenario.routingMode}
                onChange={(e) =>
                  patchScenario({ routingMode: e.target.value as Scenario["routingMode"] })
                }
                options={Object.entries(ROUTING_MODE_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </InlineField>
        </div>
      )}
      {headerExtra}
    </div>
  )
}

/** 右のペインの先頭の「期待」。正しいもの（期待）と、AI が出したもの（生成・生成結果）を並べて見る場所 */
export function ReviewCard({ children }: { children: ReactNode }) {
  return (
    <section
      aria-label="期待"
      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm"
    >
      <h2 className="text-[1.5rem] font-bold text-black">期待</h2>
      {children}
    </section>
  )
}

/**
 * 検証ケースの外枠。ヘッダー・左の督促チャット・右の先頭（期待など）・右のセクションを受け取り、督促ケース詳細と同じ枠に並べる。
 * 開いたときは、左のペインを末尾（最新の発言）まで送っておく
 */
export function CaseFrame({
  resetKey,
  header,
  chat,
  rightTop,
  sections,
  counts,
}: {
  /** 変わったらセクションの監視を張り直す（ケース・読み取り／編集の切り替え） */
  resetKey: string
  header: ReactNode
  chat: ReactNode
  rightTop: ReactNode
  sections: Record<CaseSection, ReactNode>
  counts?: Partial<Record<CaseSection, number>>
}) {
  const layout = useDetailSections(CASE_SECTIONS, [resetKey])
  useLayoutEffect(() => {
    const pane = layout.leftRef.current
    if (pane && window.matchMedia?.(DETAIL_SPLIT_QUERY)?.matches) pane.scrollTop = pane.scrollHeight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])
  return (
    <DetailFrame
      layout={layout}
      header={header}
      left={chat}
      rightTop={rightTop}
      fill
      jumpBar={
        <SectionJumpBar
          layout={layout}
          sections={CASE_SECTIONS}
          counts={counts}
          targetOf={caseSectionId}
          onJump={(value) => layout.jumpToSection(value)}
        />
      }
    >
      {CASE_SECTIONS.map(({ value, label }) => (
        <DetailSection
          key={value}
          id={caseSectionId(value)}
          label={label}
          sectionRef={layout.registerSection(value)}
        >
          {sections[value]}
        </DetailSection>
      ))}
    </DetailFrame>
  )
}
